import type {
  DomainProvenance,
  NodeSource,
  NodeKey,
  NodeUnit,
  SlotInputNode,
  SlotValuationInputsResponse,
  TypeCurveParameterStatus,
  ZoneKey,
} from 'types/packages-api/slot-valuation-inputs';
import {
  absentProvenanceNote,
  isUnfittedDeclineRow,
  isUnappliedCategoryRate,
  UNIT_HEADER_SUFFIX,
  ZONE_LABELS,
  ZONE_ORDER,
} from './utils';
import {
  appliedFromVocabulary,
  blockNotesForSection,
  capabilityRows,
  exponentialRampRowsNotUsed,
  lengthInputsNotUsed,
  NOT_USED_EXPONENTIAL_RAMP,
  NOT_USED_OWN_DECLINE_LENGTH,
  NOT_USED_UNRANKED,
  rankInputsNotUsed,
  type RowGateContext,
  waterRowsNotUsed,
} from './row-gate-utils';

/**
 * ── THE SOURCE BLOCKS ────────────────────────────────────────────────────────────────────
 *
 * The tearsheet's table view is organised by WHERE A VALUE CAME FROM, not by the order the
 * calculator happened to produce it. Each block is one zone; two of them carry structure the
 * zone alone cannot express, and both are DERIVED FROM THE REGISTRY rather than listed here:
 *
 *   Type Curve  a slot whose volumes come from its own production history has no Arps
 *               parameters, and rendering five blank rows reads as missing data. The block
 *               states its volume source kind instead. See {@link volumeSourceKind}.
 *
 *   Expenses    capital and operating costs answer different questions and are split. The
 *               split comes from the registry's own edges — see {@link classifyExpenses}.
 *
 * NOTHING HERE HARDCODES A NODE KEY INTO A GROUP. The one place a node key appears is
 * {@link EXPENSE_ROOTS}, and those are the two function nodes the registry itself declares.
 */

/** Block order. One block per zone, in the order the mock flows. */
export const SOURCE_BLOCK_ORDER: ZoneKey[] = ZONE_ORDER;

/**
 * ── royalty_revenue IS NO LONGER A DISPLAY ZONE (INS-11969) ──────────────────────────────
 *
 * Product removed the Royalty section (Royalty %, ORRI %, Lease Royalty %) from the tearsheet
 * and the export. `royalty_revenue` is dropped from {@link ZONE_ORDER}, so it renders no block
 * and its rows — the three royalty rates — never appear on the page or in the workbook. The
 * registry keeps the nodes (royalty_percentage / orri_percentage still feed the revenue
 * formula, and the fields are load-bearing across Scenarios / ARIES / the calc blob); only the
 * tearsheet DISPLAY of the zone goes.
 *
 * The ROWS need nothing beyond that omission. The SERIES do: {@link unclaimedSeriesColumns}
 * raises if a monthly column belongs to no rendered block, and older captures filed the
 * revenue monthly series under `royalty_revenue` before INS-12209 moved revenue to `cashflow`.
 * So for SERIES ATTRIBUTION ONLY, the zone folds into `cashflow` — a stray revenue column lands
 * under Cashflow (where it belongs) rather than orphaning. A no-op for a current payload, whose
 * royalty_revenue zone holds only the three (now unrendered) rates and no series.
 */
const DISPLAY_ZONE_FOLD: Partial<Record<ZoneKey, ZoneKey>> = {
  royalty_revenue: 'cashflow',
};

/**
 * The zone a node's SERIES columns are attributed to, after {@link DISPLAY_ZONE_FOLD}. Row
 * grouping does NOT use this — a row renders under its own zone, and only when that zone is in
 * {@link ZONE_ORDER}; the fold exists solely so a series column cannot orphan.
 *
 * @param {ZoneKey} zone - The node's own zone.
 * @return {ZoneKey} The zone its series columns render under (royalty_revenue -> cashflow).
 */
export function displayZone(zone: ZoneKey): ZoneKey {
  return DISPLAY_ZONE_FOLD[zone] ?? zone;
}

/**
 * The two registry function nodes the expense split is derived FROM. These are roots, not a
 * classification: which nodes belong to each is read off the graph, never listed.
 */
export const EXPENSE_ROOTS = { capex: 'capex', opex: 'opex' } as const;

export type ExpenseClass = 'identity' | 'capex' | 'opex' | 'unclassified';

/** A node paired with what the registry says it is, for the expenses block's sub-groups. */
export interface ClassifiedNode {
  node: SlotInputNode;
  expenseClass: ExpenseClass;
}

type EdgeNode = Pick<SlotInputNode, 'key' | 'inputs' | 'formula'>;

/**
 * Every node reachable UPSTREAM from `root` by following `inputs`.
 *
 * Upstream only. A bidirectional walk looks more thorough and is actively wrong: capex and
 * opex share the downstream `cost` node, so walking consumers merges them — measured on the
 * live registry, it put 21 of the expenses zone's 22 nodes in BOTH groups.
 *
 * @param {string} root - Node key to walk from.
 * @param {Map<NodeKey, EdgeNode>} byKey - Every node, addressable by key.
 * @return {Set<NodeKey>} The root and its transitive inputs.
 */
function ancestors(root: NodeKey, byKey: Map<NodeKey, EdgeNode>): Set<NodeKey> {
  const seen = new Set<NodeKey>();
  const queue: NodeKey[] = [root];
  while (queue.length) {
    const key = queue.shift() as NodeKey;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    for (const input of byKey.get(key)?.inputs ?? []) {
      queue.push(input);
    }
  }
  return seen;
}

/**
 * Nodes a root's own formula text names by key.
 *
 * The second half of the derivation, and it is not cosmetic: a function's OUTPUTS are named
 * in its formula but appear in nobody's `inputs`, so ancestors alone leaves them out.
 * `afe_cost`'s formula assigns `spud_date_dc_cost_per_well`; `opex`'s names
 * `net_oil_opex_cost`. Ancestors alone classified 15 of the zone's 22 nodes; with this, 21.
 *
 * Matched on a word boundary so `opex` cannot claim `net_oil_opex_cost` by substring — that
 * would have swept unrelated keys into whichever root ran first.
 *
 * ── THE FORMULAS SEARCHED ARE THE FAMILY'S OWN, NOT ITS SHARED UPSTREAM ────────────────
 *
 * `capex` and `opex` share a long chain of ancestors — the whole volumes chain reaches both
 * — and a shared node's formula says nothing about which branch a key belongs to. The case
 * that proved it (HISTORICAL — the node has since been removed from the registry, 25 Sep
 * 2026): when `fractionalization`, then shared by both roots, gained a formula naming the
 * three fixed-cost columns it divides, both roots claimed all three by that one text, the
 * "in both means unclassified" rule fired, and `spud_date_dc_cost_per_well` and
 * `first_production_date_dc_cost_per_well` silently left the capex group for "Other
 * expenses" — under a caption saying the model map connects them to neither, which for those
 * two was false. The committed test fixture predated the formula, so nothing failed.
 *
 * The rule below is NOT retired with the node. Any shared node that gains a formula naming a
 * leaf key reproduces this exactly, and `netting` and `volume_scaling` are still shared.
 *
 * So the caller passes the EXCLUSIVE ancestors. A key named only in shared upstream text is
 * genuinely ambiguous and lands in `unclassified`, which is the honest answer rather than
 * whichever root was walked first.
 *
 * @param {Set<NodeKey>} family - Nodes exclusive to this root, whose formulas are searched.
 * @param {Map<NodeKey, EdgeNode>} byKey - Every node, addressable by key.
 * @param {NodeKey[]} candidates - Keys eligible to be named (the zone's nodes).
 * @return {Set<NodeKey>} Candidates named in one of those formulas.
 */
function namedInFormulas(
  family: Set<NodeKey>,
  byKey: Map<NodeKey, EdgeNode>,
  candidates: NodeKey[]
): Set<NodeKey> {
  const text = [...family]
    .map((key) => byKey.get(key)?.formula ?? '')
    .join('\n');
  const named = new Set<NodeKey>();
  for (const key of candidates) {
    if (new RegExp(`\\b${key}\\b`).test(text)) {
      named.add(key);
    }
  }
  return named;
}

/**
 * Is this node an IDENTITY ROW — a label naming where a block's numbers came from, rather
 * than a term in how they were computed?
 *
 * ── THE PROPERTY IS GENERAL, AND THE REGISTRY ALREADY STATES IT ──────────────────────────
 *
 * ModelMap::EDGE_FREE_BY_DESIGN exists for exactly one shape of fact, and its own comment
 * defines it: "a value that says WHERE A NUMBER CAME FROM without being a term in how it was
 * computed". `slot_volume_source` is the founding case; `afe_well_name` and `afe_source_id`
 * were added under the same reasoning by INS-12211 ("Names the AFE the slot's capex was
 * taken from. The costs are their own nodes; this names their origin").
 *
 * So the question "is this an identity row" is already answered in the registry, and it is
 * answerable HERE without a new served field, because edge-freeness is visible in the
 * payload: a node with no `inputs`, named in nobody's `inputs`, and named in no formula
 * participates in no edge. That is the same predicate ModelMap's invariant enforces, read
 * from the other end.
 *
 * ── WHY NOT A LIST, AN EDGE, OR A FLAG ───────────────────────────────────────────────────
 *
 * A hardcoded key would break this file's rule that grouping is derived. An edge into
 * `afe_cost` would assert that the AFE's NAME is an operand of its cost, which is false and
 * would also pre-empt INS-12212, whose whole subject is where the AFE belongs in the graph.
 * A `display_group` flag on the node would put a rendering opinion in the registry.
 *
 * This asks the graph a question the graph can answer, so the next identity row someone adds
 * is grouped correctly the moment it earns its EDGE_FREE_BY_DESIGN entry — and the registry
 * invariant means it cannot be added WITHOUT one.
 *
 * ── WHAT IT DELIBERATELY DOES NOT CATCH ──────────────────────────────────────────────────
 *
 * `loe_fixed_well_cost`, the expenses zone's other oddity. It HAS an edge — INS-11969
 * declared `cost` as its real consumer — so it is not edge-free and stays `unclassified`,
 * which is the honest answer for a genuine cost whose kind the registry does not state.
 * Measured against the live registry fixture: the expenses zone has 24 nodes and exactly
 * two are edge-free.
 *
 * @param {NodeKey} key - The node to test.
 * @param {Map<NodeKey, EdgeNode>} byKey - Every node in the payload, addressable by key.
 * @param {Set<NodeKey>} connected - Keys named in some node's `inputs` or formula text.
 * @return {boolean} True when the node participates in no edge at all.
 */
function isIdentityRow(
  key: NodeKey,
  byKey: Map<NodeKey, EdgeNode>,
  connected: Set<NodeKey>
): boolean {
  return (byKey.get(key)?.inputs ?? []).length === 0 && !connected.has(key);
}

/**
 * Every node key some other node names — in its `inputs`, or by key in its formula text.
 *
 * BOTH HALVES, for the same reason {@link namedInFormulas} exists: a node named only in a
 * formula participates in the model even though no `inputs` array lists it, and treating it
 * as edge-free would call a real operand an identity label.
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @return {Set<NodeKey>} Keys that are named somewhere.
 */
function connectedKeys(slotInputs: SlotInputNode[]): Set<NodeKey> {
  const connected = new Set<NodeKey>();
  const text = slotInputs.map((node) => node.formula ?? '').join('\n');
  for (const node of slotInputs) {
    for (const input of node.inputs ?? []) {
      connected.add(input);
    }
    if (new RegExp(`\\b${node.key}\\b`).test(text)) {
      connected.add(node.key);
    }
  }
  return connected;
}

/**
 * Splits the expenses zone into capital and operating costs USING THE REGISTRY'S OWN EDGES.
 *
 * ── WHY THE OBVIOUS SIGNALS ARE WRONG ────────────────────────────────────────────────────
 * A `*_date` sibling looks like the AFE marker and classifies only 5 of 22. A `net_`/`afe_`
 * prefix rule is a naming convention, not a fact about the model, and breaks the moment a
 * node is renamed. Both encode in the frontend something the registry already states.
 *
 * ── UNCLASSIFIED IS A REAL ANSWER AND IS RETURNED, NOT SWALLOWED ─────────────────────────
 * On the live registry this returns exactly one: `loe_fixed_well_cost`. It is no longer
 * disconnected — INS-11969 declared its real consumer, `cost`, on the strength of
 * config_generator.py:186-203 and costs.py:169-181 — but `cost` is DOWNSTREAM of both roots,
 * so the edge says where the number goes and not which kind of expense it is. The calculator
 * agrees: it is a FixedCost, in neither the AFE capital allocation nor the variable
 * production costs `opex` models. Its prose calls it a lease operating expense, so a human
 * reads it as opex instantly, and THE REGISTRY STILL DOES NOT SAY SO — putting it in the
 * opex block on the strength of its prose is the hardcoding this function exists to avoid.
 * It renders in its own group and the spec pins the set so a second one cannot arrive
 * unnoticed.
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @return {ClassifiedNode[]} The expenses-zone nodes, each with its derived class.
 */
export function classifyExpenses(
  slotInputs: SlotInputNode[]
): ClassifiedNode[] {
  const byKey = new Map<NodeKey, EdgeNode>(
    slotInputs.map((node) => [node.key, node])
  );
  const zoneNodes = slotInputs.filter((node) => node.zone === 'expenses');
  const candidates = zoneNodes.map((node) => node.key);

  const capexAncestors = ancestors(EXPENSE_ROOTS.capex, byKey);
  const opexAncestors = ancestors(EXPENSE_ROOTS.opex, byKey);
  // Only a node THIS root reaches and the other does not may lend its formula text. See
  // namedInFormulas for what happened when shared upstream was allowed to.
  const family = (anc: Set<NodeKey>, other: Set<NodeKey>) => {
    const exclusive = new Set<NodeKey>(
      [...anc].filter((key) => !other.has(key))
    );
    return new Set<NodeKey>([
      ...anc,
      ...namedInFormulas(exclusive, byKey, candidates),
    ]);
  };
  const capex = family(capexAncestors, opexAncestors);
  const opex = family(opexAncestors, capexAncestors);
  const connected = connectedKeys(slotInputs);

  return zoneNodes.map((node) => {
    // IDENTITY IS TESTED FIRST, and it has to be. An identity row reaches neither root, so
    // the capex/opex test below would call it `unclassified` — which is how "AFE Name"
    // came to render under "Other expenses", beneath a caption saying the model map
    // connects it to neither kind of cost. That caption was TRUE and the conclusion wrong:
    // the row is not an unclassified cost, it is not a cost at all.
    //
    // ⚠️ EXCEPT FOR A NODE THE CALCULATOR BUCKETS. `costKind` says this thing IS a cost, so
    // it can never be an identity label however few edges it has — and "few edges" is
    // precisely the position `loe_fixed_well_cost` is in. Found by a synthetic example that
    // classified a declared, edge-free cost as 'identity': the same mistake, in the same
    // group, that an earlier caption made about the AFE name, only inverted.
    if (!node.costKind && isIdentityRow(node.key, byKey, connected)) {
      return { node, expenseClass: 'identity' as const };
    }
    const inCapex = capex.has(node.key);
    const inOpex = opex.has(node.key);
    // A node in BOTH is unclassified, not arbitrarily assigned. It does not occur today
    // (measured: zero overlap), and if it starts to, the honest answer is "the registry is
    // ambiguous here" rather than whichever branch this function tested first.
    if (inCapex === inOpex) {
      // ── ⚠️ THE DECLARED KIND RESOLVES WHAT THE EDGES LEAVE OPEN ──────────────────────
      //
      // Reached ONLY here, and that placement is the whole design. `costKind` is the
      // calculator's own bucket for a cost (carry_interest.py:359-377, transcribed in
      // ModelMap::COST_KIND); the edge walk above is what the registry says FEEDS what.
      // When the graph has an answer the graph wins, so a declared kind can never
      // contradict a real edge — it only speaks where the edges are silent, and where they
      // are silent the alternative is "Other expenses", which for a cost the calculator
      // buckets is less true than the bucket.
      //
      // This is why "LOE is missing from the Opex section" could not be answered
      // with an edge: `loe_fixed_well_cost` reaches neither root, and an edge into `opex`
      // would have asserted it is an operand of the net opex costs. It is not one.
      // So: declare the calculator's partition, and group by it.
      if (node.costKind) {
        return { node, expenseClass: node.costKind };
      }
      return { node, expenseClass: 'unclassified' as const };
    }
    return { node, expenseClass: inCapex ? 'capex' : 'opex' };
  });
}

// ── WHY IDENTITY IS ITS OWN GROUP AND NOT FOLDED INTO CAPEX (INS-12211 follow-up) ────────
//
// Andrew asked for these rows "beside the AFE costs", and the obvious reading is to put
// them IN the capex group. That would state something false: an AFE's name and record id
// are not capital expenditure, and a group captioned "Capital costs (AFE)" containing them
// asserts they are. It would also make the group's own total, if one is ever added, wrong
// by two rows that carry no money.
//
// Its own group, ordered IMMEDIATELY ABOVE capex, is adjacent — which is what "beside"
// asks for — while still saying what the rows are. The label names the relationship rather
// than the AFE, because the class is general: the next identity row the registry gains
// lands here too, whatever it identifies.
export const EXPENSE_CLASS_LABELS: Record<ExpenseClass, string> = {
  identity: 'Cost source',
  capex: 'Capital costs (AFE)',
  opex: 'Operating costs',
  unclassified: 'Other expenses',
};

/**
 * Sub-group caption. The unclassified group says WHY it exists, because "Other" with no
 * explanation reads as a dumping ground rather than as a statement about the registry.
 */
export const EXPENSE_CLASS_CAPTIONS: Record<ExpenseClass, string | null> = {
  // States the rows' standing in the model, so a reader does not read a group sitting above
  // the costs as another kind of cost.
  identity:
    'Names the record these costs were taken from. Not operands — no formula reads them.',
  capex: null,
  opex: null,
  unclassified:
    'The model map does not connect these to either capital or operating costs.',
};

/**
 * ── WHAT THE ONE VOLUME-SOURCE BLOCK CAN CLAIM ABOUT THIS SLOT ───────────────────────────
 *
 * Volume Source, PDP Decline and Type Curve were three blocks with two caption functions
 * between them; the merge makes them one block, so they become one function with four
 * outcomes covering five situations (a DCA fit and a Legacy fit share a sentence).
 *
 * ⚠️ KEYED ON THE SOURCE VOCABULARY, NOT ON `node.zone`. Both predecessors filtered
 * `node.zone === 'type_curve'` / `'pdp_decline'`, and the merge deletes both keys — they
 * would have gone on compiling and answered `unknown` for every slot on earth. The
 * vocabulary at `source[1]` is the same signal {@link isUnfittedDeclineRow} trims on, so the
 * caption and the rows it captions cannot disagree about which source produced them.
 *
 * ── STILL MEASURED FROM `applied`, NOT FROM THE SLOT'S CATEGORY ──────────────────────────
 *
 * A PDP slot carries a type-curve id like `4203332708_..._PDP_IHS` that matches NO row in
 * `type_curve_parameters` — zero of the table's 5,516 rows are PDP/IHS ids — so the id being
 * present proves nothing. Only whether a node actually resolved does. A category rule would
 * miscall a DUC with a broken lookup and a PDP with no curve alike.
 *
 * ── THE LAST TWO OUTCOMES ARE DIFFERENT FACTS AND MUST NOT SHARE A SENTENCE ──────────────
 *
 * `named_source` means the source is recorded and this RUN did not capture its parameters;
 * `unknown_source` means we do not know what the volumes came from, so we cannot say they
 * came from a forecast at all. That distinction was hard-won — see PDP_DECLINE_COPY's
 * history in git — and the merge preserves it.
 *
 * ⚠️ THE WORD "Legacy" IS NEVER PRINTED. Nothing in the payload carries it: the value of
 * `slot_volume_source` is the forecast's id, and `dca_scenario` is not a node. Printing it
 * would assert a source name the page cannot read.
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @param {Function} sourceFor - A node key to its registry source tuple.
 * @return {VolumeSourceKind} The block's claim.
 */
export type VolumeSourceKind =
  | 'type_curve'
  | 'fitted_decline'
  /**
   * ── THE SOURCE IS A TYPE CURVE AND THIS RUN HAS NONE OF ITS PARAMETERS ─────────────────
   *
   * "The PDP Decline caption still fires for a type-curve slot whose curve lookup resolved
   * nothing." It did: nothing resolved, `slot_volume_source` is set, and the fall-through
   * reached `named_source` — whose sentence says "this decline's parameters" and sends the
   * reader to the well page. A type curve is not a decline and is not on the well page, so
   * both halves of that sentence were wrong for this slot.
   *
   * Told apart by the RUN'S OWN forecast status, served on the header
   * (`SlotsBlobReader#forecast_status_for`). Nothing already in the payload could: a slot's
   * category cannot, because a volume-source override moves a PDP well onto a type curve
   * without changing it, and `volume_source_type` holds the pipeline version ('eval_3_0' on
   * all seven captures), not the kind of source.
   */
  | 'named_type_curve'
  | 'named_source'
  | 'unknown_source'
  /**
   * ⚠️ NO CLAIM — the node registry is not in hand, so the block says NOTHING.
   *
   * This classification reads each node's registry `source`, which arrives on a SECOND
   * request (`useModelMap`). Until it lands — and permanently if it fails — every lookup is
   * `undefined`, nothing matches any vocabulary, and the fall-through reached
   * `named_source`. A type-curve DUC then rendered "PDP decline: <curve name>" and "This run
   * did not record this decline's parameters": a confident, wrong sentence about a slot that
   * has no decline at all. Briefly while loading; forever after a terminal failure.
   *
   * A blank is honest and a wrong label is not, so the absence of the registry produces the
   * absence of a claim. Its copy is null and neither named header renders.
   */
  | 'unclassified';

export function volumeSourceKind(
  slotInputs: SlotInputNode[],
  sourceFor: (key: NodeKey) => NodeSource | undefined,
  registryAvailable: boolean,
  forecastStatus?: string | null
): VolumeSourceKind {
  // ⚠️ FIRST, AND NOT DERIVED FROM THE LOOKUPS. "Every lookup returned undefined" and "this
  // slot matches no vocabulary" are indistinguishable from inside this function, and one of
  // them must not be answered. The caller knows which it is; this refuses to guess.
  if (!registryAvailable) {
    return 'unclassified';
  }

  const appliedFrom = (vocabulary: string): boolean =>
    appliedFromVocabulary(slotInputs, sourceFor, vocabulary);

  // Precedence is stated rather than relied on: A1 says no slot carries both, measured at 0
  // of 686, so this order never actually decides anything. It is here so that if that ever
  // stopped being true the page would name the curve rather than pick by accident.
  //
  // ONE TYPE-CURVE STORE (INS-13307): the promoted version the run priced, under
  // `type_curve_dca_parameters`; one decline store, the DCA forecast's `dca_parameters`.
  if (appliedFrom('type_curve_dca_parameters')) {
    return 'type_curve';
  }
  if (appliedFrom('dca_parameters')) {
    return 'fitted_decline';
  }
  const source = slotInputs.find((node) => node.key === 'slot_volume_source');
  if (!source?.applied) {
    return 'unknown_source';
  }
  // ⚠️ ONLY 'type-curve' REDIRECTS, and only when the server said it. An absent status is an
  // older payload or a slot the blob has no row for, and it keeps the answer this function
  // has always given rather than guessing at the newer one. The vocabulary is the backend's
  // own ('type-curve' / 'pdp' — ConfigurationHelpers::VOLUME_SOURCE_TYPE_TO_FORECAST_STATUS),
  // matched exactly: a near-miss must read as "no claim", not as a decline.
  return forecastStatus === 'type-curve' ? 'named_type_curve' : 'named_source';
}

/**
 * ── ⚠️ A PENDING VOLUME SOURCE, AND THE PARAMETERS BENEATH IT ────────────────────────────
 *
 * "The PDP Decline parameter rows still show the old source's q_i/d_i/b/EUR under the new
 * source's name, with no pending marker."
 *
 * Both halves of that are true and neither is wrong on its own: the Volume Source row shows
 * the NEW id because the override wins the read, and the parameters below are the OLD
 * source's because they are what this run resolved and nothing has re-run. What was missing
 * is the sentence that reconciles them, so the block read as one slot's data when it is two.
 *
 * Derived from the payload the row already carries — `overridden` without `overrideApplied`,
 * which is exactly the state the row's own marker renders — rather than from a new server
 * field. Applied overrides are not pending: after the run the parameters ARE the new
 * source's.
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @return {boolean} Whether a volume-source override is recorded and not yet in the numbers.
 */
export function volumeSourceChangePending(
  slotInputs: SlotInputNode[]
): boolean {
  const node = slotInputs.find(
    (candidate) => candidate.key === 'slot_volume_source'
  );
  const value = node?.values?.[0];
  return Boolean(value?.overridden) && value?.overrideApplied !== true;
}

/**
 * ── ⚠️ THE ROWS THIS RUN DID NOT USE ("a note where formulas/values aren't in use")
 *
 * The example was a PDP showing Dev Pace offsets; the general ask is anywhere the page shows
 * a value that did not feed the result. This answers the half that is already on the page:
 * `apply_afe` false means "the AFE columns are populated but IGNORED" — the node's own prose
 * — so every value feeding `afe_cost` beside the flag is on screen and was never charged.
 *
 * ⚠️ DERIVED FROM THE SERVED GRAPH, NOT A LIST HERE. `afe_cost.inputs` is the registry's own
 * answer to "what does this cost consume", and it arrives on the node. A literal list of
 * five keys would have been wrong twice over: it is TEN rows (five costs and five dates, all
 * of them `afe_cost` feeders), and a registry change would not move it.
 *
 * ⚠️ `=== false`, NOT FALSY. An absent or null flag is not a claim that the AFE was ignored,
 * and marking ten rows on a payload that never carried the flag would be the same confident
 * wrongness this marker exists to remove.
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @return {Set<NodeKey>} The rows whose value did not reach this run's result.
 */
export function rowsNotUsedByRun(slotInputs: SlotInputNode[]): Set<NodeKey> {
  const gate = slotInputs.find((node) => node.key === 'apply_afe');
  // ── ⚠️ THE RUN'S VALUE, NOT THE PREVIEW'S ───────────────────────────────────────────
  //
  // This read `value`, which is the OVERLAID one: `cascade_afe_costs!` writes
  // `apply_afe: true` into the served blob whenever an AFE override resolves. So a pending
  // AFE pick on a slot the run priced with Apply AFE OFF flipped the gate and every marker
  // vanished — the run still charged none of those values, and the page stopped saying so
  // in exactly the case the marker exists for.
  //
  // `runValue` is served only where the overlay displaced the node, so falling back to
  // `value` is correct and is the ordinary path: with no pending pick nothing is displaced
  // and the two are the same number.
  const entry = gate?.values?.[0];
  const ranWithAfeOff = entry?.runValue ?? entry?.value;
  if (ranWithAfeOff !== false) {
    return new Set<NodeKey>();
  }
  const consumer = slotInputs.find((node) => node.key === 'afe_cost');
  return new Set<NodeKey>(
    (consumer?.inputs ?? []).filter((key) => key !== 'apply_afe')
  );
}

/**
 * ── ⚠️ THE ROWS A CAPTURED-BUT-UNSCHEDULED AREA SUPPLIED ─────────────────────────────────
 *
 * A block caption already says this — "Development pace does not schedule this slot's
 * category. Rows marked \"not used\" come from the area it falls in and were not applied" —
 * and it sits above the whole block. ⚠️ THE BLOCK IS NOT ALL AREA VALUES. Measured on the Dev Pace
 * block: alongside the area's pace model and its nine lag months sit `first_production_date`,
 * `cashflow_date`, `well_spud_date`, `slot_category` and `formation_depth` — the run's own
 * outputs and the well's own actuals, which the run DID use. Repeating the caption on every
 * row would print "not used" on those, which is worse than the caption being scanned past.
 *
 * So the rule is two conditions, both read off the payload:
 *
 *   the DOMAIN says the scenario did not apply   provenance.reason === 'captured_not_scheduled'
 *   the ROW came from that scenario's columns    served source[0] === 'column'
 *
 * ⚠️ DERIVED, NOT A LIST OF FOURTEEN. `source[0] === 'column'` is the registry's own mark for
 * a node read out of a DevelopmentPaceScenario or AreaConfig column — measured: exactly the
 * 14, and nothing else in the registry uses that source kind. A hand-written list would go
 * stale the first time an area gains a column, which is the mistake the AFE rule already made
 * once.
 *
 * ⚠️ ONLY `captured_not_scheduled`. The other reasons are DIFFERENT CLAIMS, measured:
 * `no_area_layer` means the domain has no area layer at all — its rows on ec59_pdp are the
 * revenues the run computed and the opex it charged — and `unmatched` means a match was
 * attempted and the scenario's own defaults were used and applied. Marking either would be
 * the false statement this narrowing exists to avoid.
 *
 * @param {SlotInputNode[]} nodes - The section's nodes.
 * @param {DomainProvenance | null | undefined} provenance - The section's domain provenance.
 * @param {Function} sourceFor - A node key to its served registry source.
 * @return {Set<NodeKey>} The rows the captured area supplied and the run did not apply.
 */
/**
 * Whether a block makes no provenance claim at all — it names a scenario, or it has no
 * domain to claim about.
 *
 * ⚠️ SHARED, BECAUSE THE PAGE AND THE WORKBOOK MUST AGREE. The page suppresses its caption on
 * exactly this condition, and the per-row marker has
 * to inherit it — writing the test twice is how the two come to disagree, and the marker
 * already appeared on four suppressed rows once when it was computed independently.
 *
 * @param {object} section - The zone section's identity fields.
 * @return {boolean} True when nothing about provenance may be claimed here.
 */
export function sectionMakesNoClaim(section: {
  scenario?: string | null;
  scenarioHref?: string | null;
  area?: string | null;
  domain?: string | null;
}): boolean {
  return Boolean(
    section.scenario || section.scenarioHref || section.area || !section.domain
  );
}

export function rowsFromUnappliedScenario(
  nodes: SlotInputNode[],
  provenance: DomainProvenance | null | undefined,
  sourceFor: (key: NodeKey) => NodeSource | undefined
): Set<NodeKey> {
  if (provenance?.reason !== 'captured_not_scheduled') {
    return new Set<NodeKey>();
  }
  return new Set<NodeKey>(
    nodes
      .filter((node) => {
        const source = sourceFor(node.key);
        return Array.isArray(source) && source[0] === 'column';
      })
      .map((node) => node.key)
  );
}

/** Two words: the column is narrow. */
export const NOT_USED_MARKER_LABEL = 'not used';

/** One word, beside "not used", for a row note (INS-13307). */
export const ROW_NOTE_MARKER_LABEL = 'note';

/** The toggletip behind the marker, on an AFE-gated row. Approved wording. */
export const NOT_USED_AFE_EXPLANATION =
  'Apply AFE is off for this slot, so this value was not charged to this run.';

/**
 * The toggletip behind the marker, on a row the captured area supplied.
 *
 * ── ⚠️ SPLIT FROM THE CAPTION, DELIBERATELY ──────────────────────────────────────────────
 *
 * The marker used to render the caption's own text, which kept the two in step for free.
 * Once the caption became "Rows marked \"not used\" come from the area…", reusing it put a
 * sentence about the marks INSIDE one of the marks — a toggletip describing itself. This is
 * the same claim, addressed to one row.
 *
 * Derived from the caption's sentence.
 */
export const NOT_USED_CAPTURED_AREA =
  'This value comes from the area this slot falls in and was not applied to it.';

/**
 * ── THE ONE NOT-USED GATE, READ BY THE PAGE AND THE WORKBOOK ─────────────────────────────
 *
 * Every row of one block that is marked "not used", with the sentence its marker carries.
 * Two sources, and the area one wins where both apply:
 *
 *   the AFE rows    {@link rowsNotUsedByRun} — the run priced this slot with Apply AFE off
 *   the area rows   {@link rowsFromUnappliedScenario} — ONLY where the block's caption
 *                   shows, i.e. the block makes a claim ({@link sectionMakesNoClaim}) AND
 *                   absentProvenanceNote has a sentence for it
 *
 * ⚠️ GATED ON THE CAPTION, NOT ON THE PROVENANCE ALONE. A row marker that appears where its
 * block's caption is suppressed is a claim the block refuses to make. The workbook used to
 * test only the first half of that, so a provenance whose status carries no caption (matched,
 * global_default) could mark rows in the file that the page left unmarked.
 *
 * @param {object} section - The block: its nodes, provenance and identity fields.
 * @param {SlotInputNode[]} allNodes - Every node in the payload, for the AFE gate.
 * @param {Function} sourceFor - A node key to its served registry source.
 * @param {RowGateContext} [context] - The header facts and served definitions, including
 *   `capture` (`header.provenanceCapture`) for the area caption test.
 * @return {Map<NodeKey, string>} Marked row keys, each to its marker's explanation.
 */
export function notUsedRowsForSection(
  section: {
    nodes: SlotInputNode[];
    provenance: DomainProvenance | null | undefined;
    scenario?: string | null;
    scenarioHref?: string | null;
    area?: string | null;
    domain?: string | null;
  },
  allNodes: SlotInputNode[],
  sourceFor: (key: NodeKey) => NodeSource | undefined,
  context: RowGateContext = {}
): Map<NodeKey, string> {
  const marked = new Map<NodeKey, string>();
  // INS-13307: the rows a slot's water rule, category, volume route or the registry rule out.
  // Set first, so the AFE and area markers below keep the last word where both apply.
  for (const [key, explanation] of waterRowsNotUsed(
    allNodes,
    sourceFor,
    context
  )) {
    marked.set(key, explanation);
  }
  for (const key of rankInputsNotUsed(allNodes)) {
    marked.set(key, NOT_USED_UNRANKED);
  }
  for (const key of lengthInputsNotUsed(allNodes, context.forecastStatus)) {
    marked.set(key, NOT_USED_OWN_DECLINE_LENGTH);
  }
  // Never over another reason: on a ratio slot the water ramp was not valued at all, unramped or not.
  for (const key of exponentialRampRowsNotUsed(allNodes)) {
    if (!marked.has(key)) {
      marked.set(key, NOT_USED_EXPONENTIAL_RAMP);
    }
  }
  // A run whose calculator did not price a ramp-up or a daily q_i: over the exponential
  // marker, which describes what a calculator that could would have done, and under every other. A
  // run that did not record it gets a row note instead (rowNotesForSection).
  for (const [key, note] of capabilityRows(context, 'not_declared')) {
    if (!marked.has(key) || marked.get(key) === NOT_USED_EXPONENTIAL_RAMP) {
      marked.set(key, note);
    }
  }
  for (const key of rowsNotUsedByRun(allNodes)) {
    marked.set(key, NOT_USED_AFE_EXPLANATION);
  }
  const captionShown =
    !sectionMakesNoClaim(section) &&
    absentProvenanceNote(section.provenance, context.capture) !== null;
  if (captionShown) {
    for (const key of rowsFromUnappliedScenario(
      section.nodes,
      section.provenance,
      sourceFor
    )) {
      marked.set(key, NOT_USED_CAPTURED_AREA);
    }
  }
  return marked;
}

/**
 * The note that reconciles the two.
 */
export const PENDING_VOLUME_SOURCE_NOTE =
  'A volume source change is recorded for this slot and is not in these numbers yet. The parameters below are the ones this run used.';

export const VOLUME_SOURCE_COPY: Record<VolumeSourceKind, string | null> = {
  type_curve: 'Volumes come from a type curve. The parameters below are its.',
  // One sentence for DCA and Legacy alike. It says what DID happen: "no type curve
  // parameters" describes an absence and invites the reader to treat correct output as a
  // defect.
  fitted_decline:
    "Volumes come from this well's own production forecast. The parameters below are its fitted decline.",
  // ⚠️ THIS DOES NOT MEAN "THE DECLINE HAS NO PARAMETERS". A Legacy decline
  // carries a full ModArps curve — the well page has always shown it — and the run simply
  // never wrote it down. The run records it now, so a slot only lands here when the
  // recording did not happen: the source_id resolved to no forecast, or to one with no
  // fitted stream, or the blob predates the change. This wording is approved.
  // ⚠️ NOT THE DECLINE SENTENCE, AND NOT A LINK TO THE WELL PAGE. The volumes
  // came from a type curve whose parameters this run did not resolve — the curve is not this
  // well's decline and is not on its well page, so neither half of the sentence below applies.
  named_type_curve:
    'Volumes come from a type curve. This run did not record its parameters.',
  named_source:
    "This run did not record this decline's parameters. They are on the well page.",
  unknown_source:
    "This slot's volume source was not recorded, so there are no decline parameters to show.",
  // Nothing is claimed without the registry — see the type's own note.
  unclassified: null,
};

/**
 * The type-curve caption when the rows are the promoted version this run priced (INS-13307):
 * `typeCurveParameterStatus` as_priced or changed_since_run. The second adds its own block note
 * (TYPE_CURVE_CHANGED_SINCE_RUN_NOTE) rather than a second caption.
 */
export const TYPE_CURVE_AS_PRICED_COPY =
  'Volumes come from a type curve. The parameters below are the version this run priced.';

/**
 * The type-curve caption when the run recorded no version for the slot's source (INS-13307,
 * `typeCurveParameterStatus` version_not_recorded): a composite wellbore, whose legs are priced
 * each on its own curve. No rows are shown, so the caption says why rather than "did not record
 * its parameters", which reads as a gap in this run alone.
 */
export const TYPE_CURVE_VERSION_NOT_RECORDED_COPY =
  'Volumes come from a type curve. Type-curve version not recorded for this run.';

/**
 * The merged block's caption: {@link VOLUME_SOURCE_COPY} for the kind, refined where the payload
 * says more (INS-13307). Read by the page and the workbook, so the two print one sentence.
 *
 *   legacySourceMessage  the server's sentence, whatever the kind (INS-13307): the run priced
 *                   this slot from an old source (a Legacy PDP forecast, a type curve's pre-baked
 *                   volumes), so no parameters are shown, only why.
 *   type_curve      by `typeCurveParameterStatus`: the version the run priced.
 *                   version_not_recorded (a composite) says so, here and on a named_type_curve block.
 *                   No status (not served, or an older payload) keeps the kind's sentence.
 *
 * @param {(VolumeSourceKind|null)} kind - The block's kind; null off the volumes block.
 * @param {(TypeCurveParameterStatus|null)} [status] - `header.slot.typeCurveParameterStatus`.
 * @param {(string|null)} [legacySourceMessage] - `header.slot.legacySourceMessage`.
 * @return {(string|null)} The caption, or null when the block claims nothing.
 */
export function volumeSourceCaption(
  kind: VolumeSourceKind | null,
  status?: TypeCurveParameterStatus | null,
  legacySourceMessage?: string | null
): string | null {
  if (!kind) {
    return null;
  }
  if (legacySourceMessage) {
    return legacySourceMessage;
  }
  const typeCurveBlock = kind === 'type_curve' || kind === 'named_type_curve';
  if (typeCurveBlock && status === 'version_not_recorded') {
    return TYPE_CURVE_VERSION_NOT_RECORDED_COPY;
  }
  if (
    kind === 'type_curve' &&
    (status === 'as_priced' || status === 'changed_since_run')
  ) {
    return TYPE_CURVE_AS_PRICED_COPY;
  }
  return VOLUME_SOURCE_COPY[kind];
}

/**
 * A block's captions, in the page's order: the volume source caption, the pending Volume Source
 * note (volumes only), then the block notes. The page renders one paragraph each and the workbook
 * joins them into the block's Source cell, so the two can't drift (the workbook's
 * copy had lost {@link PENDING_VOLUME_SOURCE_NOTE}).
 *
 * @param {object} section - The block: its zone, volume source kind and monthly columns.
 * @param {SlotInputNode[]} renderedRows - The block's rendered rows ({@link renderedRowNodes}).
 * @param {SlotInputNode[]} allNodes - Every node in the payload.
 * @param {object} context - The served payload (`response`, for the header facts) and a node
 *   key to its served source and unit.
 * @return {string[]} The captions; empty when the block has none.
 */
export function blockCaptionsForSection(
  section: {
    zone: ZoneKey;
    volumeSource: VolumeSourceKind | null;
    seriesColumns: NodeKey[];
  },
  renderedRows: SlotInputNode[],
  allNodes: SlotInputNode[],
  context: {
    response: SlotValuationInputsResponse;
    sourceFor: (key: NodeKey) => NodeSource | undefined;
    unitFor: (key: NodeKey) => NodeUnit | undefined;
  }
): string[] {
  const { response, ...lookups } = context;
  const slot = response.header?.slot;
  const typeCurveParameterStatus = slot?.typeCurveParameterStatus;
  return [
    volumeSourceCaption(
      section.volumeSource,
      typeCurveParameterStatus,
      slot?.legacySourceMessage
    ),
    section.zone === 'volumes' && volumeSourceChangePending(allNodes)
      ? PENDING_VOLUME_SOURCE_NOTE
      : null,
    ...blockNotesForSection(renderedRows, section.seriesColumns, allNodes, {
      ...lookups,
      typeCurveParameterStatus,
    }),
  ].filter((note): note is string => note !== null);
}
/**
 * ── THE FORMULA PLACEMENT RULE ───────────────────────────────────────────────────────────
 *
 * A formula printed at the top of a block reads as governing every row beneath it. That is
 * true only when the formula's node actually derives them.
 *
 * So a formula is promoted to block level ONLY when its node's upstream family covers every
 * other value row in the block. Otherwise it stays on its own row, where it makes a claim
 * about one node and nothing else.
 *
 * @param {SlotInputNode[]} blockNodes - The nodes rendered in one block.
 * @return {SlotInputNode[]} Those whose formula may stand at block level. Usually empty.
 */
export function blockLevelFormulaNodes(
  blockNodes: SlotInputNode[]
): SlotInputNode[] {
  const byKey = new Map<NodeKey, EdgeNode>(
    blockNodes.map((node) => [node.key, node])
  );
  const withFormula = blockNodes.filter((node) => Boolean(node.formula));
  return withFormula.filter((candidate) => {
    const family = ancestors(candidate.key, byKey);
    return blockNodes.every(
      (node) => node.key === candidate.key || family.has(node.key)
    );
  });
}

/**
 * ── NODES RELOCATED TO OWNERSHIP INTEREST FROM THEIR NATIVE ZONE (INS-11965 UAT) ─────────
 *
 * Andrew asked for the Ownership Interest section to carry a line item for both the net
 * revenue interest AND the working interest — beside NRI, where a reader goes to answer
 * "what do we own". The NRI already lives in this zone (INS-12209); the working interest
 * does not. `net_working_interest` is a `volumes`-zone node, because that is where it is
 * USED — it scales the net fixed costs, not the volumes' ownership split (see its prose and
 * the model_map banner). Its SERVER zone is unchanged, so it still draws in the volumes zone
 * on the model map; what changes here is only its row's DISPLAY in the table and export.
 *
 * ── RELOCATED, NOT DUPLICATED ────────────────────────────────────────────────────────────
 *
 * The row used to render in BOTH places (its native Volume Source block and Ownership
 * Interest). The export is a scrutability document, and one input appearing under two blocks
 * reads as two separate inputs that happen to agree — so the row MOVES to the block it
 * belongs to (Ownership Interest, beside the NRI) and is dropped from its native block. The
 * table follows the export so the page and the workbook never disagree.
 *
 * ── THIS IS A DECLARED KEY, AND ALMOST NOTHING ELSE IN THIS FILE IS ──────────────────────
 *
 * Every grouping rule here is derived from the registry because the registry states the
 * fact it needs. This one cannot be: there is no signal in the graph that says "show this
 * row under a different heading" — it is a layout request from Product, the same shape as
 * {@link WITHDRAWN_ROWS}. So it is declared, once, with its reason, rather than smuggled in
 * as a flag on the node.
 *
 * A row is relocated only when the Ownership Interest block ALREADY EXISTS (has a native
 * node of its own) to host it. A payload that predates the zone renders no ownership block,
 * so the native row is KEPT rather than lost — the same "no empty block" rule every zone
 * follows.
 */
export const OWNERSHIP_INTEREST_KEYS: NodeKey[] = ['net_working_interest'];

/**
 * Renders a zone's rows with the Ownership Interest relocation applied.
 *
 *   - The Ownership Interest block gains the {@link OWNERSHIP_INTEREST_KEYS}, spliced in
 *     directly after `net_revenue_interest` (Andrew's ask: beside the NRI); if that row is
 *     absent they are appended rather than lost.
 *   - Every OTHER zone has those keys REMOVED from its own block, so the row appears once —
 *     under Ownership Interest — and never in two blocks at once.
 *
 * SHARED SO THE TABLE AND THE EXPORT AGREE. The table builds its rows from a zone grouping
 * of its own (buildSections) and the export from these blocks, so the relocation has to be
 * one function both call — a second copy is how the workbook comes to disagree with the page.
 *
 * @param {ZoneKey} zone - The block's zone.
 * @param {SlotInputNode[]} zoneNodes - The zone's own nodes, in payload order.
 * @param {SlotInputNode[]} visible - Every non-suppressed node, to source the relocation from.
 * @param {boolean} ownershipExists - Whether an Ownership Interest block will render. Guards
 *   the removal: with no host block the native row is kept rather than dropped.
 * @return {SlotInputNode[]} The nodes to render, with the relocation applied.
 */
export function withOwnershipRelocation(
  zone: ZoneKey,
  zoneNodes: SlotInputNode[],
  visible: SlotInputNode[],
  ownershipExists: boolean
): SlotInputNode[] {
  if (zone === 'ownership_interest') {
    const present = new Set(zoneNodes.map((node) => node.key));
    const relocated = OWNERSHIP_INTEREST_KEYS.filter(
      (key) => !present.has(key)
    ).flatMap((key) => {
      const node = visible.find((candidate) => candidate.key === key);
      return node ? [node] : [];
    });
    if (relocated.length === 0) {
      return zoneNodes;
    }
    const out: SlotInputNode[] = [];
    let inserted = false;
    for (const node of zoneNodes) {
      out.push(node);
      if (node.key === 'net_revenue_interest') {
        out.push(...relocated);
        inserted = true;
      }
    }
    if (!inserted) {
      out.push(...relocated);
    }
    return out;
  }
  // The relocated keys belong under Ownership Interest, so drop them from their native block
  // — but only when that block exists to host them, so a payload predating the zone keeps its
  // native row rather than losing it. net_working_interest is still sourced for the
  // relocation from `visible`, which carries it regardless of this filter.
  if (!ownershipExists) {
    return zoneNodes;
  }
  const drop = new Set<NodeKey>(OWNERSHIP_INTEREST_KEYS);
  return zoneNodes.filter((node) => !drop.has(node.key));
}

/** One rendered source block. */
export interface SourceBlock {
  zone: ZoneKey;
  label: string;
  nodes: SlotInputNode[];
  /** Populated for the expenses block only; empty elsewhere. */
  groups: Array<{ expenseClass: ExpenseClass; nodes: SlotInputNode[] }>;
  /** Formulas that legitimately govern the whole block. Usually empty. */
  blockFormulas: SlotInputNode[];
  /** Set on the merged volume-source block only; null on every other zone. */
  volumeSource: VolumeSourceKind | null;
}

/**
 * Builds the ten source blocks from a payload.
 *
 * A zone with no nodes is OMITTED rather than rendered empty — an empty block asserts "we
 * looked and there is nothing", which we cannot tell apart from "this payload predates the
 * zone". Absence says less and is the only thing we can support.
 *
 * @param {SlotValuationInputsResponse} response - The tearsheet payload.
 * @param {NodeKey[]} suppressed - Keys another view owns; excluded here so no row renders twice.
 * @param {Function} sourceFor - A node key to its registry source tuple, for the merged
 *   block's caption. The caller that has the definitions passes them.
 * @param {boolean} registryAvailable - Whether the node registry has landed. False (the
 *   default) makes the block claim nothing rather than guess — see VolumeSourceKind.
 * @return {SourceBlock[]} Blocks in {@link SOURCE_BLOCK_ORDER}, empties dropped.
 */
export function buildSourceBlocks(
  response: SlotValuationInputsResponse,
  suppressed: NodeKey[] = [],
  sourceFor: (key: NodeKey) => NodeSource | undefined = () => undefined,
  // ⚠️ DEFAULTS TO FALSE, AND THAT IS THE POINT. The `sourceFor` default above answers
  // `undefined` for every node, which used to make a caller that omitted it classify every
  // slot as `named_source` — a confident wrong label from an omitted argument. Defaulting
  // this to false means the same omission now yields `unclassified`, which claims nothing.
  registryAvailable = false
): SourceBlock[] {
  const drop = new Set(suppressed);
  const visible = response.slotInputs.filter((node) => !drop.has(node.key));
  const byZone = new Map<ZoneKey, SlotInputNode[]>();
  for (const node of visible) {
    const list = byZone.get(node.zone) ?? [];
    list.push(node);
    byZone.set(node.zone, list);
  }

  const ownershipExists = (byZone.get('ownership_interest')?.length ?? 0) > 0;
  const blocks: SourceBlock[] = [];
  for (const zone of SOURCE_BLOCK_ORDER) {
    const native = byZone.get(zone);
    if (!native?.length) {
      continue;
    }
    // Ownership Interest carries the working interest, relocated from its native (volumes)
    // block so the row shows once rather than in two blocks — see withOwnershipRelocation. A
    // no-op for every zone but ownership_interest and the relocation's source.
    const nodes = withOwnershipRelocation(
      zone,
      native,
      visible,
      ownershipExists
    );
    // Classification runs over the FULL payload, not the visible subset: a suppressed node
    // can still be the edge that connects a visible one to its root.
    const groups =
      zone === 'expenses'
        ? groupExpenses(classifyExpenses(response.slotInputs), drop)
        : [];
    blocks.push({
      zone,
      label: ZONE_LABELS[zone],
      nodes,
      groups,
      blockFormulas: blockLevelFormulaNodes(nodes),
      // The source vocabulary comes off the node DEFINITIONS, not off the values — the
      // same `response.nodes[key].source` the row trimming reads.
      volumeSource:
        zone === 'volumes'
          ? volumeSourceKind(
              visible,
              sourceFor,
              registryAvailable,
              response.header?.slot?.forecastStatus
            )
          : null,
    });
  }
  return blocks;
}

/**
 * Orders the expense sub-groups and drops empty ones.
 *
 * @param {ClassifiedNode[]} classified - Every expenses-zone node with its class.
 * @param {Set<NodeKey>} drop - Keys suppressed from this view.
 * @return {Array<{expenseClass: ExpenseClass, nodes: SlotInputNode[]}>} Non-empty groups.
 */
function groupExpenses(classified: ClassifiedNode[], drop: Set<NodeKey>) {
  // Identity first: the rows naming the record head the costs they identify.
  const order: ExpenseClass[] = ['identity', 'capex', 'opex', 'unclassified'];
  return order
    .map((expenseClass) => ({
      expenseClass,
      nodes: classified
        .filter((c) => c.expenseClass === expenseClass && !drop.has(c.node.key))
        .map((c) => c.node),
    }))
    .filter((group) => group.nodes.length > 0);
}

// ── THE MONTHLY SERIES, TRANSPOSED ───────────────────────────────────────────────────────

// ── SERIES_ROW_LIMIT IS GONE (UAT 10, 9 Sep) ─────────────────────────────────────────────
//
// It was 24, and its own comment said "TWENTY-FOUR IS A VIEW, NOT A LIMIT ON THE DATA" —
// correct, and exactly why removing it changed no fetch. Andrew asked for every month with a
// scrollbar and rejected pagination, so SeriesDisclosure renders `series.rows` whole and
// `.seriesScroll` scrolls vertically.
//
// Deleted rather than set to Infinity: a limit constant nobody applies is the dead
// declaration this file's neighbours keep being cleaned of. The caption needs no special
// case — seriesDisclosureCaption already reads "361 months" when shown >= total.

// UNIT_HEADER_SUFFIX lives in utils.ts beside formatNodeValue, so row-gate-utils can state a
// unit in a sentence (the Volume Adjustment Ratio note) without importing this module back.

/**
 * A column header: the node's label with its unit appended.
 *
 * A COLUMN OF NUMBERS WITH NO UNIT IS THE CHEAPEST WAY TO STATE SOMETHING FALSE — 19 of EC
 * 61's 34 series columns are USD and 6 are BBL, and they are visually identical.
 *
 * @param {string} label - The node's registry label.
 * @param {NodeUnit} [unit] - Its declared unit; undefined until the model map loads.
 * @return {string} "Gross Oil (BBL)", or the bare label when no unit can be stated.
 */
export function seriesColumnHeader(label: string, unit?: NodeUnit): string {
  const suffix = unit ? UNIT_HEADER_SUFFIX[unit] : null;
  return suffix ? `${label} (${suffix})` : label;
}

/**
 * The series columns belonging to one block, DERIVED from zone membership.
 *
 * Not a per-zone list: a column belongs to the block whose zone its own registry node
 * declares. Adding a monthly node to the registry puts its column in the right block with no
 * frontend change, which is the same property the capex/opex split has.
 *
 * @param {ZoneKey} zone - The block's zone.
 * @param {SlotValuationInputsResponse} response - The tearsheet payload.
 * @return {NodeKey[]} That block's columns, in the payload's column order.
 */
export function seriesColumnsForZone(
  zone: ZoneKey,
  response: SlotValuationInputsResponse
): NodeKey[] {
  const zoneOf = new Map<NodeKey, ZoneKey>(
    response.slotInputs.map((node) => [node.key, displayZone(node.zone)])
  );
  return (response.outputs?.cashflowSeries?.columns ?? []).filter(
    (column) => zoneOf.get(column) === zone
  );
}

/**
 * Series columns that no rendered block will show.
 *
 * ── THE LOUD FAILURE, AND WHY IT IS NOT A SILENT FILTER ──────────────────────────────────
 *
 * A column whose node is absent from `slotInputs`, or whose zone produced no block, would
 * simply never render. That is data the calculator produced and the page quietly dropped —
 * the same class of gap as the disconnected `loe_fixed_well_cost`. The spec asserts this is
 * empty against the live payload, so a new monthly node in an unrendered zone fails a test
 * rather than vanishing.
 *
 * @param {SlotValuationInputsResponse} response - The tearsheet payload.
 * @param {SourceBlock[]} blocks - The blocks that will actually render.
 * @return {NodeKey[]} Columns claimed by nothing.
 */
export function unclaimedSeriesColumns(
  response: SlotValuationInputsResponse,
  blocks: SourceBlock[]
): NodeKey[] {
  const rendered = new Set(blocks.map((block) => block.zone));
  const zoneOf = new Map<NodeKey, ZoneKey>(
    response.slotInputs.map((node) => [node.key, displayZone(node.zone)])
  );
  return (response.outputs?.cashflowSeries?.columns ?? []).filter((column) => {
    const zone = zoneOf.get(column);
    return !zone || !rendered.has(zone);
  });
}

// ── WHAT THE DISCLOSURE REPLACES, AND WHAT IT DOES NOT ───────────────────────────────────

/**
 * Every series column rendered in some block's disclosure on this page.
 *
 * This is the set that decides whether a node's inline four-value preview is redundant. It
 * is DERIVED from the blocks actually being rendered, not from a list of node keys, so a
 * zone that stops rendering automatically gives its nodes their previews back.
 *
 * @param {SourceBlock[]} blocks - The blocks that will render.
 * @param {SlotValuationInputsResponse} response - The tearsheet payload.
 * @return {Set<NodeKey>} Column keys a reader can see in full.
 */
export function disclosedSeriesKeys(
  blocks: SourceBlock[],
  response: SlotValuationInputsResponse
): Set<NodeKey> {
  return new Set(
    blocks.flatMap((block) => seriesColumnsForZone(block.zone, response))
  );
}

/**
 * Monthly nodes a reader can see NEITHER as an inline preview NOR as a disclosure column.
 *
 * ── THE LOUD FAILURE, AND THE REASON IT EXISTS ───────────────────────────────────────────
 *
 * Deleting the inline previews trades one presentation for another, and the trade is only
 * safe where the replacement is actually on the page. A monthly node whose zone renders no
 * block loses its row AND has no column — it becomes invisible, and nothing about that is
 * detectable by looking at the page, which is precisely how a value goes missing quietly.
 *
 * Asserted empty against the live payload, the same way `unclaimedSeriesColumns` is.
 *
 * @param {SlotValuationInputsResponse} response - The tearsheet payload.
 * @param {SourceBlock[]} blocks - The blocks that will render.
 * @return {NodeKey[]} Monthly node keys with no rendering at all.
 */
export function invisibleMonthlyNodes(
  response: SlotValuationInputsResponse,
  blocks: SourceBlock[]
): NodeKey[] {
  const renderedZones = new Set(blocks.map((block) => block.zone));
  const disclosed = disclosedSeriesKeys(blocks, response);
  return response.slotInputs
    .filter((node) => node.seriesKey)
    .filter(
      (node) =>
        !renderedZones.has(displayZone(node.zone)) &&
        !disclosed.has(node.seriesKey as NodeKey)
    )
    .map((node) => node.key);
}

/**
 * The nodes that get a PARAMETER ROW in a block, once the disclosure has taken over.
 *
 * ── THE SAME RULE AS THE PREVIEW, ONE LEVEL UP ───────────────────────────────────────────
 *
 * 968cef311f dropped a disclosed node's inline four-value PREVIEW. This drops its ROW. The
 * mock does exactly this: Volume Source's parameter table holds only scalars — qᵢ, b, Dᵢ,
 * forecast start — and every monthly stream lives in the disclosure. Listing a node as a row
 * whose only content is a `monthly · 361` tag, directly above a table with that node as a
 * column, states nothing the column header does not.
 *
 * DERIVED FROM THE SAME SET, NOT A SECOND NOTION OF "DISCLOSED". `disclosedSeriesKeys` is
 * the one answer to "is this node's series on the page", and it is computed from the blocks
 * actually rendering. A block that stops rendering gives its nodes their rows back.
 *
 * ── WHAT THE ROW CARRIED THAT THE HEADER DID NOT, AND WHY IT IS SAFE NOW ─────────────────
 *
 * Measured across all 45 valued slots readable locally, 34 distinct monthly nodes:
 *
 *   formula          THREE nodes carry one — oil_realized_price, gas_realized_price,
 *                    ngl_realized_price, on 45/45 slots. The row was the ONLY place they
 *                    appeared. They are now rendered in the column header (see
 *                    SeriesDisclosure's formulaFor), so this is a relocation and not a
 *                    deletion. That relocation is the precondition for this function.
 *   applied: false   ONE node — water_gas_ratio_in_bbl_per_mmcf, seriesLength 0 on 45/45.
 *                    The header already states it, via columnExtentNote's "no months".
 *   category         NONE. No monthly node carries a reserve category, so no monthly row
 *                    was ever de-emphasised by isUnappliedCategoryRate.
 *   overrides        NONE EXIST. SlotInputNode has no override field of any kind.
 *
 * And 0 of the 34 lacked a disclosure column, so no node loses a row without gaining one —
 * which `invisibleMonthlyNodes` asserts against the live payload independently.
 *
 * ── WHY THERE IS NO COMPANION `hasParameterRows` ─────────────────────────────────────────
 *
 * There was one, answering "does this block still have a row?" from the monthly rule alone.
 * A second rule then joined it — a row scoped to another reserve category does not render
 * either (see ZoneSectionView's rowsFor) — and the helper could not see it, so a block could
 * still have rendered a table head with no body. A function that answers a NARROWER question
 * than its caller needs is worse than no function: it reads as authoritative. The caller now
 * tests the length of the list it is about to render, which cannot disagree with itself.
 *
 * @param {SlotInputNode[]} nodes - The block's nodes, in payload order.
 * @param {Set<NodeKey>} disclosed - From {@link disclosedSeriesKeys}.
 * @return {SlotInputNode[]} Those still needing a row of their own.
 */
export function parameterRowNodes(
  nodes: SlotInputNode[],
  disclosed: Set<NodeKey>
): SlotInputNode[] {
  return nodes.filter(
    (node) => !(node.seriesKey && disclosed.has(node.seriesKey))
  );
}

/**
 * Rows WITHDRAWN FROM THE TEARSHEET BY PRODUCT DECISION, each against its reason.
 *
 * ── THIS IS A LIST OF KEYS, AND EVERYTHING ELSE IN THIS FILE REFUSES TO BE ───────────────
 *
 * `classifyExpenses` walks edges, `parameterRowNodes` reads `seriesKey`, `rowsFor` compares
 * reserve categories — every other rule here is DERIVED, because every other rule states a
 * property of the model that the registry already knows. This one cannot be, and pretending
 * otherwise would be worse than declaring it: there is no fact in the registry that
 * distinguishes a row Product wants from a row Product has stopped wanting. Inventing a
 * `display: false` flag on the node would put a rendering opinion inside the graph, which is
 * the direction this codebase has consistently refused (see ModelMap's header: the registry
 * is the honest picture of the CALCULATION).
 *
 * So it is declared, here, once — and it carries its REASON in the value rather than being a
 * bare array, so a reader who finds a missing row has the ticket in front of them instead of
 * a key with no story.
 *
 * ── THE NODE STAYS IN THE REGISTRY. ITS EDGE NO LONGER DOES ─────────────────────────────
 *
 * Written when `fractionalization` still existed: deleting the node would
 * have deleted a real edge and left the registry describing a division it no longer names.
 * The ask then was about the tearsheet's ROW, so the row was what went.
 *
 * ⚠️ UPDATED. `type_curve_blending` and
 * `fractionalization` themselves have since come out of the registry. So
 * `fractionalized_months` now has no consumer node and no edge — it is on
 * EDGE_FREE_BY_DESIGN with the removal as its stated reason.
 *
 * THE NODE ITSELF STILL STAYS, and for the original reason unchanged: the calculator still
 * divides by it, four times, at fractionalization.py:129-190, reached from
 * vol_orchestrator.py:222-225. The registry keeps the node so it can still NAME a value the
 * calculation uses; it no longer draws an arrow, because the node that consumed it is gone
 * from the display. The full record of that divergence is in model_map.rb, above `netting`.
 */
export const WITHDRAWN_ROWS: Record<NodeKey, string> = {
  fractionalized_months:
    'INS-12207. Product is moving away from fractionalization, so the count no longer earns ' +
    'a row of its own. Its consumer node was removed outright on 25 Sep 2026; the node is ' +
    'kept because the calculator still divides by it.',
};

/**
 * The nodes a block ACTUALLY RENDERS AS ROWS, after the three rules that remove one.
 *
 * ── WHY THIS IS A FUNCTION AND NOT A CLOSURE IN THE VIEW ─────────────────────────────────
 *
 * It used to be `rowsFor`, defined inside ZoneSectionView. The Excel export (UAT 11) has to
 * produce EXACTLY the rows the table shows — same set, same order — and the only way to
 * guarantee that is for both to call one function. A second copy of these rules living in
 * the exporter is how a workbook comes to disagree with the page it was taken from, which
 * is the failure this whole epic exists around.
 *
 * That is also why the withdrawal rule lands HERE and not in the view: the table
 * (tearsheet-table-view.tsx) and the workbook (tearsheet-export.ts) are the two callers, so
 * a row removed here cannot survive in one and not the other.
 *
 * THREE RULES. THE FIRST TWO ARE DERIVED; THE THIRD IS DECLARED AND SAYS SO:
 *
 *   1. A node whose monthly series is disclosed on this page gets no row of its own — the
 *      disclosure column IS its row. See {@link parameterRowNodes}.
 *   2. A rate scoped to another reserve category is not an input to this slot. See
 *      isUnappliedCategoryRate, which FAILS OPEN: an unresolved category on either side
 *      keeps the row rather than silently dropping twelve.
 *   3. A row Product has withdrawn. See {@link WITHDRAWN_ROWS} for why this one cannot be
 *      derived and must not become a flag on the node.
 *
 * @param {SlotInputNode[]} nodes - The block's nodes, in payload order.
 * @param {Set<NodeKey>} disclosed - From {@link disclosedSeriesKeys}.
 * @param {Function} categoryFor - A node key to its served reserve category.
 * @param {(string|null)} slotCategory - This slot's own reserve category.
 * @param {Function} sourceFor - A node key to its registry source tuple. Used only by the
 *   PDP Decline rule, which is keyed on the blob vocabulary a node reads from.
 * @return {SlotInputNode[]} The rows the block renders.
 */
export function renderedRowNodes(
  nodes: SlotInputNode[],
  disclosed: Set<NodeKey>,
  categoryFor: (key: NodeKey) => string | undefined,
  slotCategory: string | null,
  sourceFor: (key: NodeKey) => NodeSource | undefined = () => undefined
): SlotInputNode[] {
  return (
    parameterRowNodes(nodes, disclosed)
      .filter(
        (node) => !isUnappliedCategoryRate(categoryFor(node.key), slotCategory)
      )
      // A PDP Decline parameter for a stream this forecast does not carry — see
      // isUnfittedDeclineRow for why this zone alone removes rather than fading.
      .filter(
        (node) =>
          !isUnfittedDeclineRow(node.key, sourceFor(node.key), node.applied)
      )
      .filter((node) => !(node.key in WITHDRAWN_ROWS))
  );
}

/**
 * A note for ONE column whose own extent differs from the table's.
 *
 * ── WHY THIS EXISTS: THE BLOCK CAPTION CANNOT SPEAK FOR EVERY COLUMN ─────────────────────
 *
 * `series_points` drops nulls per node (node_binder.rb:305-314), so `seriesLength` is a
 * PER-NODE non-null count while the disclosure caption is per block. Measured on EC 64 and
 * EC 61: six of seven blocks have one shared count, and the volumes block does not —
 * `water_gas_ratio_in_bbl_per_mmcf` carries seriesLength 0 and has zero non-null values
 * across all 362 (361) rows, while its nine neighbours are full.
 *
 * So the header's "first 24 of 362 months" was FALSE for that column, which has no months at
 * all. Removing the per-node captions without this would have deleted the only place that
 * fact appeared. The note renders only where the column disagrees with the table, so the
 * common case stays quiet and the exception is stated where the reader is looking at it.
 *
 * @param {number} [seriesLength] - The column's own non-null count.
 * @param {number} rowCount - Rows in the series.
 * @return {string | null} The note, or null when the block caption already covers it.
 */
export function columnExtentNote(
  seriesLength: number | undefined,
  rowCount: number
): string | null {
  // Undefined is UNKNOWN, not zero. A node that never declared a length must not be
  // captioned as empty — that would assert a fact we do not have.
  if (seriesLength === undefined || seriesLength === null) {
    return null;
  }
  // >= NOT ===. The note's job is to say a column is SHORTER than the table. A count at or
  // above the row count has nothing to disclose, and subtracting anyway produces sentences
  // like "361 of 30 months" — which a truncated fixture produced on the first run here.
  if (seriesLength >= rowCount) {
    return null;
  }
  if (seriesLength === 0) {
    return 'no months';
  }
  return `${seriesLength} of ${rowCount} months`;
}
